import React from 'react';
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import { goToStudioDetailsPage } from '@gp/utils/studio';
import { FIELDS2, FIELDS, FIELD_NAMES, getMetricInfoListUI } from '@gp/utils/metricTips';
import { getDataTimes } from '@gp/pages/StudioMonthlyReport/utils';
import { DAU, DAU_KEY, YOY_DAU_KEY, MOM_DAU_KEY, RES_FIELDS_MAP } from '../constants';

import type { IRecord } from '@gp/types/global';
import type { LiveTitleDataType } from '../types/services';

const getMetricInfoListUICommon = (data: IRecord<any>) => getMetricInfoListUI(FIELDS, FIELD_NAMES, data);

const getActualTdOpt = (
  valueKey: string,
  yoyKey: string,
  momKey: string,
  isSelectYoy: boolean,
  isSelectMom: boolean,
) => {
  let tendencyOpt: any = null;
  if (isSelectYoy && isSelectMom) {
    tendencyOpt = {
      type: 'tendency',
      value: yoyKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'YoY',
      isRenderDoubleTendency: true,
      doubleTendencyValue: momKey,
      doubleLabel: 'MoM',
    };
  } else if (isSelectYoy) {
    tendencyOpt = {
      type: 'tendency',
      value: yoyKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'YoY',
    };
  } else if (isSelectMom) {
    tendencyOpt = {
      type: 'tendency',
      value: momKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'MoM',
    };
  }

  return {
    number: {
      options: [
        {
          type: 'value',
          value: valueKey,
          isTitle: true,
          isFormat: true,
          style: { maxWidth: 70 },
        },
        tendencyOpt,
      ],
    },
  };
};
const getBudgetCompletionTdOpt = (valueKey: string, percentKey: string) => {
  return {
    number: {
      options: [
        {
          type: 'value',
          value: valueKey,
          isTitle: true,
          isFormat: true,
          style: { maxWidth: 120 },
        },
        {
          type: 'percent',
          value: percentKey,
          isFormat: true,
          style: { marginLeft: 10, color: '#888' },
        },
      ],
    },
  };
};

const getTableRecords = (loading: boolean, data: LiveTitleDataType) => {
  if (loading) return [];
  const list = data?.list;
  const records = [];
  if (Array.isArray(list)) {
    let allStudioItem = null;
    for (let i = 0, l = list?.length; i < l; i++) {
      const t = list?.[i];
      const item: { [keyName: string]: string | number | undefined | null } = {
        uniqueId: `${t?.studio_id}_${i}${1}`,
        cover: t?.cover,
        game_name: t?.game_name,
        studio_id: t?.studio_id,
        studio_name: t?.studio_name,
        bo: t?.bo,
        genre: t?.genre,
        sub_genre: t?.sub_genre,
        biz_model: t?.biz_model,
        platform: Array.isArray(t?.platform) ? t?.platform?.join(',') : t?.platform,
        select_date_total_gross_revenue: null,
        select_date_total_gross_revenue_yoy: null,
        select_date_total_gross_revenue_mom: null,
        select_date_total_mau: null,
        select_date_total_mau_yoy: null,
        select_date_total_mau_mom: null,
        [DAU_KEY]: null,
        [YOY_DAU_KEY]: null,
        [MOM_DAU_KEY]: null,
        select_date_total_arpu: null,
        select_date_total_arpu_yoy: null,
        select_date_total_arpu_mom: null,
        select_date_total_hc: null,
        select_date_total_net_profit: null,
        select_date_total_net_profit_completion: null,
        release_cost_rate: null,
        staff_month_rate: null,
      };
      const details = t?.details;
      if (Array.isArray(details)) {
        for (let j = 0, l = details?.length; j < l; j++) {
          const k = details?.[j];
          const key = RES_FIELDS_MAP?.[k?.metric]?.key;
          if (key) {
            item[key] = k?.value;
            if (RES_FIELDS_MAP?.[k?.metric]?.yoy) {
              item[`${key}_yoy`] = k?.yoy;
            }
            if (RES_FIELDS_MAP?.[k?.metric]?.mom) {
              item[`${key}_mom`] = k?.mom;
            }
          }
        }
      }
      if (item?.studio_name === 'All Studios') {
        allStudioItem = item;
        continue;
      }
      records.push(item);
    }
    if (allStudioItem) {
      records.unshift(allStudioItem);
    }
  }

  return records;
};

const getTableOptions: (
  loading: boolean,
  data: LiveTitleDataType,
  tableHeaderDate: string,
  yoy: boolean,
  mom: boolean,
) => any = (loading, data, tableHeaderDate, yoy, mom) => {
  const date = tableHeaderDate ? tableHeaderDate?.slice(0, 7) : '-';
  const dataTimes = getDataTimes(data?.metrics_data_time);

  return {
    columsOptions: [
      {
        keyName: 'project',
        title: 'Project',
        isHasDoubleTitle: true,
        width: 240,
        align: 'left',
        fixed: 'left',
        tdOpt: {
          project: {
            domain: 'game',
            src: 'cover',
            title: 'game_name',
            platform: 'platform',
            platformPlaceHolder: '-',
            isVisibleFixedCustomBubble: true,
            bubbleClassName: 'dbg-studio-monthly-report-live-title-list-project-title-bubble',
            bubbleStyle: { width: 'auto' },
            renderCustomBubbleContent: (cvm: any) => {
              return (
                <>
                  <strong>{cvm?.game_name || '-'}</strong>
                  <p style={{ opacity: 0.86 }}>Studio: {cvm?.studio_name || '-'}</p>
                  <p style={{ opacity: 0.86 }}>Genre: {cvm?.genre || '-'}</p>
                  <p style={{ opacity: 0.86 }}>Biz Model: {cvm?.biz_model || '-'}</p>
                </>
              );
            },
          },
        },
      },
      {
        keyName: 'studio',
        title: 'Studio',
        isHasDoubleTitle: true,
        width: 180,
        align: 'left',
        // fixed: 'left',
        tdOpt: {
          customize: {
            render: (cvm: any) => {
              return (
                <EllipsisTextBubble
                  text={cvm?.studio_name || '-'}
                  textElClassName="dbg-hover-active"
                  onTextClick={(e) => {
                    e?.stopPropagation();
                    goToStudioDetailsPage({ id: cvm?.studio_id, name: cvm?.studio_name });
                  }}
                />
              );
            },
          },
        },
      },
      {
        keyName: 'select_date_total_gross_revenue',
        title: `${date} Total\nGross Revenue($)`,
        isTips: true,
        renderTips: () => {
          const grossRevenueDataTime = dataTimes?.['Gross Revenue'] || '-';
          const data = {
            definition: 'Cash Received From Players',
            source: 'Reporting Package',
            updateCadence: 'Monthly',
            dataTime: grossRevenueDataTime,
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 206,
        align: 'right',
        tdOpt: getActualTdOpt(
          'select_date_total_gross_revenue',
          'select_date_total_gross_revenue_yoy',
          'select_date_total_gross_revenue_mom',
          yoy,
          mom,
        ),
      },
      {
        keyName: 'select_date_total_mau',
        title: `${date} Total\nMAU`,
        isTips: true,
        renderTips: () => {
          const mauDataTime = dataTimes?.MAU || '-';
          const data = {
            definition:
              'Represents the total number of active users who interact with a product, service, or platform at least once within a 30-day period.',
            source: 'Dashboard, Studio Monthly Live Title Report',
            updateCadence: 'Monthly',
            dataTime: mauDataTime,
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 206,
        align: 'right',
        tdOpt: getActualTdOpt(
          'select_date_total_mau',
          'select_date_total_mau_yoy',
          'select_date_total_mau_mom',
          yoy,
          mom,
        ),
      },
      {
        keyName: DAU_KEY,
        title: `${date} Avg\n DAU`,
        isTips: true,
        renderTips: () => {
          const dauDataTime = dataTimes?.[DAU] || '-';
          const data = {
            definition:
              'Represents the average number of unique users who engage with a product, service, or platform daily over a period (such as a month), divided by the number of days in that period.',
            source: 'Dashboard, Studio Monthly Live Title Report',
            updateCadence: 'Monthly',
            dataTime: dauDataTime,
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 206,
        align: 'right',
        tdOpt: getActualTdOpt(DAU_KEY, YOY_DAU_KEY, MOM_DAU_KEY, yoy, mom),
      },
      {
        keyName: 'select_date_total_arpu',
        title: `${date} Total\nARPU($)`,
        isTips: true,
        renderTips: () => {
          const arppuDataTime = dataTimes?.ARPU || '-';
          const data = {
            definition:
              'Represents the overall average revenue generated from all users during a specific period. It is calculated by dividing the total revenue by the total number of active users.',
            source: 'Dashboard, Studio Monthly Live Title Report',
            updateCadence: 'Monthly',
            dataTime: arppuDataTime,
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 206,
        align: 'right',
        tdOpt: getActualTdOpt(
          'select_date_total_arpu',
          'select_date_total_arpu_yoy',
          'select_date_total_arpu_mom',
          yoy,
          mom,
        ),
      },
      {
        keyName: 'select_date_total_net_profit',
        title: `${date} Total Net\nProfit($)(Profit Ratio)`,
        isTips: true,
        renderTips: () => {
          const fields = ['definition1', 'definition2', ...FIELDS2];
          const names = { ...FIELD_NAMES, definition1: FIELD_NAMES.definition, definition2: FIELD_NAMES.definition };
          const data = {
            definition1: 'Net Profit: Excludes all below line items, such as interest expenses and taxes.',
            definition2: 'Profit Ratio: Net Profit / Gross Revenue = (Gross Revenue - Project Cost) / Gross Revenue',
            source: 'Reporting Package',
            updateCadence: 'Monthly',
            // 这里暂时没数据，前端固定展示-
            dataTime: '-',
          };
          return getMetricInfoListUI(fields, names, data);
        },
        isHasDoubleTitle: true,
        width: 230,
        align: 'right',
        tdOpt: getBudgetCompletionTdOpt('select_date_total_net_profit', 'select_date_total_net_profit_completion'),
      },
      {
        keyName: 'release_cost_rate',
        title: 'Release\nCost Rate(%)',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'Monthly Staff Cost / In House Headcount',
            source: 'Calculate',
            updateCadence: 'Quarterly',
            // 这里暂时没数据，前端固定展示-
            dataTime: '-',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 160,
        align: 'right',
        tdOpt: {
          number: {
            options: [
              {
                type: 'percent',
                value: 'release_cost_rate',
                isFormat: true,
              },
            ],
          },
        },
      },
      {
        keyName: 'select_date_total_hc',
        title: `${date} HC`,
        isTips: true,
        renderTips: () => {
          const hcDataTime = dataTimes?.HC || '-';
          const data = {
            definition:
              'Includes Research & Development headcount, Non Research & Development headcount (e.g. Sales and Marketing, Management, Functional Support, Customer Service).',
            source: 'Rolling Forecast',
            updateCadence: 'Quarterly',
            dataTime: hcDataTime,
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 160,
        align: 'right',
        tdOpt: {
          number: {
            options: [
              {
                type: 'value',
                value: 'select_date_total_hc',
                isTitle: true,
                isKeepInt: true,
              },
            ],
          },
        },
      },
      {
        keyName: 'staff_month_rate',
        title: 'Staff Month\nRate($)',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'Monthly Staff Cost / In House Headcount',
            source: 'Calculate',
            updateCadence: 'Quarterly',
            // 这里暂时没数据，前端固定展示-
            dataTime: '-',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 165,
        align: 'right',
        tdOpt: {
          number: {
            options: [
              {
                type: 'value',
                value: 'staff_month_rate',
                isTitle: true,
                isFormat: true,
              },
            ],
          },
        },
      },
    ],
    tableBodyOptions: {
      isAutoadaptationBodyHeight: true,
      outerContainerClassName: 'tea-layout__content',
      subtractHeight: 295,
    },
    sortableOptions: {
      sortColumns: [
        'studio',
        'project',
        'select_date_total_gross_revenue',
        'select_date_total_mau',
        DAU_KEY,
        'select_date_total_arpu',
        'select_date_total_net_profit',
        'release_cost_rate',
        'select_date_hc',
        'headcounts_rate',
      ],
      defaultSort: {
        by: 'select_date_total_gross_revenue',
        order: 'desc',
      },
    },
    pageableOptions: {
      pageSizeOptions: [10, 20, 50, 100],
    },
  };
};

export { getTableRecords, getTableOptions };
